import React from "react";
import { Link } from "react-router-dom";
import PageShell from "@/components/site/PageShell";
import useSEO from "@/lib/useSEO";
import { ArrowRight, Handshake, Users, FileCheck, Repeat, Shield, BookOpenCheck, LineChart, MessageSquare } from "lucide-react";

const CPA_IMG = "https://images.pexels.com/photos/12903168/pexels-photo-12903168.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const VALUE = [
  { i: Handshake, t: "Specialty Tax Expertise on Demand", d: "We bring engineering depth, IRS-tested methodologies, and decades of specialty-tax experience to every engagement — exactly when you need it." },
  { i: Users, t: "Engineers, Not Salespeople", d: "Our staff are licensed engineers, CPAs, and former Big-4 specialty tax professionals. You get technical depth, not a referral mill." },
  { i: FileCheck, t: "Audit-Ready Deliverables", d: "Every report we produce passes IRS scrutiny: detailed engineering documentation, citations, photos, and contemporaneous narratives." },
  { i: Repeat, t: "Recurring Referral Revenue", d: "Generous referral fee on every study. Multi-year arrangements available. Paid quarterly, transparently." },
  { i: Shield, t: "Full Audit Defense Included", d: "If the IRS examines any study we delivered, we defend it at no additional cost to you or your client." },
  { i: BookOpenCheck, t: "Continuing Education", d: "We host private CPE training for our partner firms on cost segregation, R&D credits, energy incentives, and federal/state updates." },
];

const HOW = [
  { n: "01", t: "Identify Opportunities", d: "We work with you to identify clients with property over ~$500K or technical activities — across your client base." },
  { n: "02", t: "Free Preliminary Review", d: "We deliver a no-cost feasibility review on every referral. Only the qualified opportunities move forward." },
  { n: "03", t: "Engineering Execution", d: "We do the heavy lifting: site visits, engineering review, technical interviews, documentation, IRS-ready reports — coordinated directly with you and your client." },
  { n: "04", t: "Deliver the Win Together", d: "You present the results to your client alongside our engineering report. You keep the relationship and we deliver the specialty work." },
];

const FOR_WHOM = [
  "Solo CPAs and small partnerships looking to differentiate",
  "Mid-sized regional firms wanting specialty tax capabilities without hiring engineers",
  "Wealth advisors and family offices serving high-net-worth real estate investors",
  "Top-100 firms needing engineering bandwidth without hiring internally",
];

export default function ForCPAs() {
  useSEO({
    title: "For CPAs & Tax Professionals | Specialty Tax Partnerships | IncentTax",
    description:
      "Partner with IncentTax for cost segregation and R&D tax credit studies. Engineering-backed deliverables, full audit defense, referral revenue, and white-glove coordination with your firm.",
    canonical: "https://incenttax.com/for-tax-professionals",
  });
  return (
    <PageShell testid="cpa-page">
      {/* HERO */}
      <section className="bg-forest-900 text-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-24 sm:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <p className="text-brass uppercase tracking-[0.28em] text-sm mb-6">For Tax Professionals</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.04] tracking-tight">
              The specialty-tax partner <span className="italic text-brass">your firm has been missing.</span>
            </h1>
            <p className="mt-8 text-ivory/85 text-lg sm:text-xl leading-relaxed max-w-2xl">
              IncentTax is the engineering partner CPA firms across the
              country rely on. We deliver cost segregation studies, R&D tax
              credit studies, and other specialty tax work — collaboratively,
              with audit-ready documentation and full audit defense.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <a href="/#contact" className="inline-flex items-center justify-center gap-2 bg-brass hover:bg-brass-500 text-forest-900 px-8 py-4 font-medium" data-testid="cpa-cta-apply">
                Apply to Partner Program <ArrowRight size={18} />
              </a>
              <a href="#how" className="inline-flex items-center justify-center gap-2 border border-ivory/40 hover:bg-ivory hover:text-forest-900 px-8 py-4 font-medium transition-colors">
                How it works
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="relative">
              <img src={CPA_IMG} alt="CPA partnership" className="w-full h-[420px] object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* WHY PARTNER */}
      <section className="py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">Why CPAs partner with us</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 mb-12 max-w-4xl leading-tight">
            Built for collaboration. <span className="italic text-brass">Not competition.</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-ivory-400 border border-ivory-400">
            {VALUE.map(({ i: Icon, t, d }) => (
              <div key={t} className="bg-white p-8 hover:bg-ivory-100 transition-colors">
                <Icon className="text-brass mb-5" size={28} strokeWidth={1.5} />
                <div className="font-serif text-2xl text-forest-900 mb-3">{t}</div>
                <p className="text-charcoal leading-relaxed text-sm">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="py-24 bg-ivory-200">
        <div className="max-w-7xl mx-auto px-6 sm:px-8">
          <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">How it works</p>
          <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 mb-12 max-w-4xl leading-tight">
            A simple partnership. <span className="italic text-brass">Built for your workflow.</span>
          </h2>
          <ol className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-ivory-400 border border-ivory-400">
            {HOW.map((s) => (
              <li key={s.n} className="bg-white p-8">
                <div className="font-serif text-5xl text-brass mb-4">{s.n}</div>
                <div className="font-serif text-xl text-forest-900 mb-3">{s.t}</div>
                <p className="text-charcoal text-sm leading-relaxed">{s.d}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FOR WHOM */}
      <section className="py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-forest-700 uppercase tracking-[0.24em] text-sm mb-4">Who this is for</p>
            <h2 className="font-serif text-4xl sm:text-5xl text-forest-900 leading-tight">
              From solo to <span className="italic text-brass">top-100.</span>
            </h2>
          </div>
          <ul className="lg:col-span-7 space-y-4">
            {FOR_WHOM.map((x) => (
              <li key={x} className="flex gap-4 items-start bg-white border border-ivory-400 p-6">
                <Users className="text-brass shrink-0 mt-1" size={20} />
                <span className="text-charcoal text-lg">{x}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-forest-900 text-ivory">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 text-center">
          <MessageSquare className="text-brass mx-auto mb-6" size={40} strokeWidth={1.5} />
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ivory leading-tight mb-6">
            Ready to become a partner?
          </h2>
          <p className="text-ivory/80 text-lg mb-10 max-w-2xl mx-auto">
            We onboard a limited number of new CPA partners each quarter. Tell us a little about your firm and we'll be in touch within one business day.
          </p>
          <Link to="/#contact" className="inline-flex items-center justify-center gap-2 bg-brass hover:bg-brass-500 text-forest-900 px-10 py-4 font-medium" data-testid="cpa-bottom-cta">
            Apply Now <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </PageShell>
  );
}
